import { InstallationData } from "@/installation/installation";
import useOnPeriods, { OnPeriod } from "@/installation/onPeriods";
import useOnPeriodTemperatureAnomalies from "@/installation/useOnPeriodTemperatureAnomalies";
import { useState, useRef } from "react";
import { DateRange, LAST_24_HOURS } from "@/dateRanges/dateRanges";
import { ONE_MINUTE, TEN_SECONDS, WindowSize } from "@/dateRanges/windowSizes";
import { DateRangeContext } from "@/dateRanges/DateRangeContext";
import { OFF, RefreshInterval } from "@/dateRanges/refreshIntervals";
import { CollapsibleCard } from "@/opportunity/opportunity_detail_page/CollapsibleCard";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { CalendarDateRangePicker } from "@/components/date-range-picker";
import { ChartCard } from "@/components/chart-card";
import { ChartRef } from "@/components/chart";
import { ApiPath } from "@/api/path";
import { TSMetric } from "@/query/query";
import { OnPeriodTemperatureAnomaliesResponse, MeasurementClassification } from "@/installation/useOnPeriodTemperatureAnomalies";

const formatDateString = (dateStr: string) => {
  return format(parseISO(dateStr), "MMM d, yyyy h:mm:ss a")
}

type OnPeriodCardProps = {
  asset: InstallationData
  period: OnPeriod
  index: number
}

const OnPeriodCard = ({ asset, period, index }: OnPeriodCardProps) => {
  // Create a custom date range for this specific on period
  const onPeriodDateRange: DateRange = {
    label: `On Period ${index + 1}`,
    value: {
      from: () => new Date(period.on),
      to: () => new Date(period.off),
      live: false
    }
  }
  
  const analyzeAnomaliesMutation = useOnPeriodTemperatureAnomalies();
  const [analysisResult, setAnalysisResult] = useState<OnPeriodTemperatureAnomaliesResponse | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const chartRef = useRef<ChartRef>(null);
  const [selectedSensorRange, setSelectedSensorRange] = useState<string | null>(null);
  const [selectedRangeValues, setSelectedRangeValues] = useState<{min: number; max: number} | null>(null);
  
  const analyzeAnomalies = async () => {
    try {
      setIsAnalyzing(true);
      const result = await analyzeAnomaliesMutation.mutateAsync({
        installation_id: asset.id,
        start: period.on,
        stop: period.off,
        window_size: TEN_SECONDS.value
      });
      setAnalysisResult(result);
    } catch (error) {
      console.error("Failed to analyze anomalies:", error);
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  const highlightSensorRange = (sensorName: string, minValue: number, maxValue: number) => {
    // If same sensor is clicked again, clear the selection
    if (sensorName === selectedSensorRange) {
      setSelectedSensorRange(null);
      setSelectedRangeValues(null);
    } else {
      // Update selected sensor and range values
      setSelectedSensorRange(sensorName);
      setSelectedRangeValues({ min: minValue, max: maxValue });
    }
  };
  
  return (
    <CollapsibleCard 
      key={`${period.on}-${index}`}
      title={`${formatDateString(period.on)} - ${formatDateString(period.off)}`}
      defaultOpen={false}
    >
      <div className="p-4 space-y-6">
        <ChartCard
          className="w-full"
          title="Refrigerant loop temperatures"
          yAxisName="Temperature"
          height={350}
          unit="°C"
          apiPath={ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY}
          query={[{
            metrics: [TSMetric.ALL_TEMPERATURES],
            windowSize: TEN_SECONDS,
            dateRange: onPeriodDateRange,
            organization_id: asset.location.organization_id,
            installation_id: [asset.id],
            target_unit: "degC"
          }]}
          refreshInterval={OFF}
          canShowTemperatureOnPrimaryYAxis
          clampYAxisToData
          chartRef={chartRef}
          highlightYAxisRange={selectedRangeValues ? {
            min: selectedRangeValues.min,
            max: selectedRangeValues.max,
            label: selectedSensorRange ? `${selectedSensorRange} Expected Range` : undefined
          } : undefined}
        />
        
        <div className="space-y-4">
          {selectedSensorRange && selectedRangeValues && (
            <div className="flex items-center justify-between bg-blue-50 p-2 rounded-md">
              <span className="text-blue-700 font-medium">
                Highlighting expected range for <strong>{selectedSensorRange}</strong>: {selectedRangeValues.min.toFixed(1)}°C - {selectedRangeValues.max.toFixed(1)}°C
              </span>
              <button 
                onClick={() => {
                  setSelectedSensorRange(null);
                  setSelectedRangeValues(null);
                }}
                className="px-2 py-1 text-xs bg-white hover:bg-gray-100 text-blue-700 border border-blue-300 rounded-md"
              >
                Clear Highlight
              </button>
            </div>
          )}
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg">Temperature Analysis</h3>
            <button
              onClick={analyzeAnomalies}
              disabled={isAnalyzing}
              className={`px-4 py-2 rounded-md ${isAnalyzing ? 'bg-gray-300' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
            >
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </button>
          </div>
          
          {analysisResult === null ? (
            <div className="text-gray-500">Click analyze to analyze temperatures in this on period.</div>
          ) : analysisResult.sensors.length === 0 ? (
            <div className="text-green-600 font-medium">No sensor data available.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2 text-left border">Sensor</th>
                    <th className="p-2 text-left border">Expected Range</th>
                    <th className="p-2 text-left border">Min</th>
                    <th className="p-2 text-left border">P5</th>
                    <th className="p-2 text-left border">Mean</th>
                    <th className="p-2 text-left border">Median</th>
                    <th className="p-2 text-left border">P95</th>
                    <th className="p-2 text-left border">Max</th>
                  </tr>
                </thead>
                <tbody>
                  {analysisResult.sensors.map((sensor, index) => {
                    const getMeasurement = (type: string) => sensor.measurements.find(m => m.type === type);
                    
                    const minMeasurement = getMeasurement('min');
                    const p5Measurement = getMeasurement('p5');
                    const meanMeasurement = getMeasurement('mean');
                    const medianMeasurement = getMeasurement('median');
                    const p95Measurement = getMeasurement('p95');
                    const maxMeasurement = getMeasurement('max');
                    
                    const getClassificationBadge = (classification: MeasurementClassification) => {
                      if (classification === 'too_high') {
                        return <span className="ml-1 px-1 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">High</span>;
                      } else if (classification === 'too_low') {
                        return <span className="ml-1 px-1 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">Low</span>;
                      }
                      return null;
                    };
                    
                    const renderMeasurement = (measurement: any) => {
                      if (!measurement) return null;
                      return (
                        <>
                          {measurement.value.toFixed(1)}°C
                          {measurement.classification !== 'normal' && measurement.classification !== 'unknown' && 
                            getClassificationBadge(measurement.classification)
                          }
                        </>
                      );
                    };

                    return (
                      <tr 
                        key={index} 
                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${selectedSensorRange === sensor.sensor ? 'bg-blue-100' : ''} cursor-pointer hover:bg-blue-50`}
                        onClick={() => highlightSensorRange(sensor.sensor, sensor.predicted_range.min, sensor.predicted_range.max)}
                      >
                        <td className="p-2 border font-medium">{sensor.sensor}</td>
                        <td className="p-2 border bg-blue-50 hover:bg-blue-100 cursor-pointer" onClick={() => highlightSensorRange(sensor.sensor, sensor.predicted_range.min, sensor.predicted_range.max)}>
                          <div className="flex items-center justify-between">
                            <span>{sensor.predicted_range.min.toFixed(1)}°C - {sensor.predicted_range.max.toFixed(1)}°C</span>
                            <span className="text-xs text-blue-700">{selectedSensorRange === sensor.sensor ? '(highlighted)' : 'click to highlight'}</span>
                          </div>
                        </td>
                        <td className="p-2 border">{renderMeasurement(minMeasurement)}</td>
                        <td className="p-2 border">{renderMeasurement(p5Measurement)}</td>
                        <td className="p-2 border">{renderMeasurement(meanMeasurement)}</td>
                        <td className="p-2 border">{renderMeasurement(medianMeasurement)}</td>
                        <td className="p-2 border">{renderMeasurement(p95Measurement)}</td>
                        <td className="p-2 border">{renderMeasurement(maxMeasurement)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </CollapsibleCard>
  )
}

export const OnPeriods = ({ asset }: { asset: InstallationData }) => {
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const onPeriodsData = useOnPeriods([asset.id], shouldFetchData);
  
  const handleLookupClick = () => {
    setShouldFetchData(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center gap-2">
          <h3 className="font-medium whitespace-nowrap">Date Range:</h3>
          <CalendarDateRangePicker className="flex-1" />
        </div>
        <div className="flex-1"></div>
        <Button 
          onClick={handleLookupClick}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          disabled={shouldFetchData && onPeriodsData.isPending}
        >
          {shouldFetchData && onPeriodsData.isPending ? "Looking up on periods..." : "Look up On Periods"}
        </Button>
      </div>
      
      {shouldFetchData ? (
        <div className="space-y-2">
          {onPeriodsData.isPending ? (
            <div>Loading on periods...</div>
          ) : onPeriodsData.assets.length === 0 ? (
            <div>No on period data available</div>
          ) : onPeriodsData.assets[0].onPeriods.length === 0 ? (
            <div>No on periods found for {asset.name}</div>
          ) : (
            onPeriodsData.assets[0].onPeriods.map((period, index) => (
              <OnPeriodCard 
                key={`${period.on}-${index}`}
                asset={asset}
                period={period}
                index={index}
              />
            ))
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          Click "Look up On Periods" to search for on periods in the selected date range.
        </div>
      )}
    </div>
  )
}

export const OnPeriodAnalysisPage = ({ asset }: { asset: InstallationData }) => {

  const [onPeriodsDateRange, setOnPeriodsDateRange] = useState<DateRange>(LAST_24_HOURS);
  const [onPeriodsWindowSize, setOnPeriodsWindowSize] = useState<WindowSize>(ONE_MINUTE);
  const [refreshInterval, setRefreshInterval] = useState<RefreshInterval>(OFF);

  return (
    <DateRangeContext.Provider value={{
      dateRange: onPeriodsDateRange,
      setDateRange: setOnPeriodsDateRange,
      windowSize: onPeriodsWindowSize,
      setWindowSize: setOnPeriodsWindowSize,
      refreshInterval: refreshInterval,
      setRefreshInterval: setRefreshInterval
    }}>
      <div className="p-4">
        <OnPeriods asset={asset}/>
      </div>
    </DateRangeContext.Provider>
  )
}